import { useCallback, useEffect, useRef, useState } from "react";
import { captureException } from "@/lib/analytics";

interface UseAutoSaveOptions<T> {
  onSave: (data: T) => Promise<void>;
  timeout?: number;
}

interface UseAutoSaveReturn<T> {
  autoSaving: boolean;
  queueEdit: (data: T) => void;
  deferEdit: (data: T) => void;
  resumeDeferredEdit: () => void;
  flushPendingEdits: () => Promise<void>;
}

/**
 * Debounced auto-save hook. Edits queued via `queueEdit` are batched and saved
 * once the caller stops editing for `timeout` ms. Edits queued via `deferEdit`
 * wait without a timer until `resumeDeferredEdit`, `queueEdit` or
 * `flushPendingEdits`.
 *
 * Only the newest queued data is ever saved, and every save is chained behind
 * the previous one, so requests never overlap and the last edit always wins.
 * A pending edit is flushed on unmount, so leaving the screen within the
 * debounce window does not drop it; `flushPendingEdits` exposes the same flush
 * for explicit exit paths. Note: the unmount flush is a fire-and-forget network
 * request suited to in-app navigation — it does not cover hard page reloads or
 * tab close (use `navigator.sendBeacon` / a `keepalive` fetch for those).
 */
export function useAutoSave<T>({
  onSave,
  timeout = 1000,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const [autoSaving, setAutoSaving] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Newest queued data; null means nothing is pending.
  const pendingDataRef = useRef<T | null>(null);
  // Single chain that serializes every save so PUTs never overlap.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const isMountedRef = useRef(true);

  // Keep the latest onSave in a ref so performSave can stay a stable identity,
  // which keeps the unmount effect from re-running on every onSave change.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Chain a save of the latest pending data after any in-flight save. Because
  // saves are serialized, the unmount flush can never race an earlier PUT and
  // last-write-wins holds even when the update path replaces fields wholesale.
  const performSave = useCallback(() => {
    saveChainRef.current = saveChainRef.current
      .catch(() => {})
      .then(async () => {
        const data = pendingDataRef.current;
        if (data === null) {
          return;
        }
        pendingDataRef.current = null;

        if (isMountedRef.current) {
          setAutoSaving(true);
        }
        try {
          await onSaveRef.current(data);
        } catch (error) {
          captureException(error as Error);
          // Re-queue for retry on the next edit/flush, unless a newer edit has
          // already replaced it.
          if (pendingDataRef.current === null) {
            pendingDataRef.current = data;
          }
        } finally {
          if (isMountedRef.current) {
            setAutoSaving(false);
          }
        }
      });
    return saveChainRef.current;
  }, []);

  const resumeDeferredEdit = useCallback(() => {
    if (pendingDataRef.current === null) {
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(performSave, timeout);
  }, [performSave, timeout]);

  const queueEdit = useCallback(
    (data: T) => {
      pendingDataRef.current = data;
      resumeDeferredEdit();
    },
    [resumeDeferredEdit],
  );

  const deferEdit = useCallback((data: T) => {
    pendingDataRef.current = data;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const flushPendingEdits = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await performSave();
  }, [performSave]);

  // On unmount, chain a final save of any pending edit after the in-flight one
  // instead of dropping it. No-ops when nothing is pending.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      performSave();
    };
  }, [performSave]);

  return {
    autoSaving,
    queueEdit,
    deferEdit,
    resumeDeferredEdit,
    flushPendingEdits,
  };
}
